<template>
  <b-container class="my-5">
    <b-row v-if="notFound">
      <b-col xl="12">
        <b-card :title="$t('doctor.not_found')">
          <b-card-text>{{ $t('doctor.not_found_text') }}</b-card-text>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="doctor">
      <b-col xl="3">
        <b-card no-body>
          <b-card-body>
            <b-card-img :src="doctor.avatar.secure_url" center="true" width="125" />
            <p class="text-center text-primary h5">{{ doctor.fullName }}</p>
            <p class="text-center text-secondary" v-for="spe in office.specialities" v-bind:key="spe"> {{ $t(`specialities.${spe}`) }}</p>
            <b-card-text text-tag="div" class="mt-5">
              <p>{{ $t('doctor.phone') }}<br/><span class="text-secondary">{{ office.phone }}</span></p>
              <p>{{ $t('doctor.email') }}<br /><span class="text-secondary">{{ office.email }}</span></p>
              <p>{{ $t('doctor.appointments_cost') }}<br /><span class="text-secondary">{{ office.price }}</span></p>
              <p>{{ $t('doctor.location') }}<br /><span class="text-secondary">{{ office.shortAddress }}</span></p>
            </b-card-text>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col xl="9">
        <b-card no-body>
          <b-card-header header-bg-variant="white" header-tag="h5">{{ $t('doctor.offices') }}</b-card-header>
          <b-card-body>
            <b-input-group>
              <template v-slot:prepend>
                <b-input-group-text >
                  <font-awesome-icon :icon="['fas','hospital']"/>
                </b-input-group-text>
              </template>
              <b-form-select
                :value="office._id"
                :options="offices"
                @change="onChangeOffice"
                text-field="shortAddress"
                value-field="_id">
                <template v-slot:first>
                  <option value="null" disabled>{{ $t('doctor.labels.office') }}</option>
                </template>
              </b-form-select>
            </b-input-group>
          </b-card-body>
        </b-card>
        <b-card no-body class="my-3">
          <b-card-header header-bg-variant="white" header-tag="h5">{{ $t('doctor.cv') }}</b-card-header>
          <b-card-body>
            <b-card-text>{{ doctor.bio }}</b-card-text>
          </b-card-body>
          <hr class="my-0">
          <b-card-body>
            <h5>Horarios de consulta</h5>
            <b-table responsive :items="table.items" :fields="table.headers"></b-table>
          </b-card-body>
          <hr class="my-0">
          <b-card-body v-if="office._id">
            <b-img class="img-fluid rounded" :src="'https://maps.googleapis.com/maps/api/staticmap?center=' + office.location.coordinates[1].toString() + ',' + office.location.coordinates[0].toString() + '&zoom=15&size=800x400&markers=color:blue%7Clabel:D%7C' + office.location.coordinates[1].toString() + ',' + office.location.coordinates[0].toString() + '&key=' + googleMapsApiKey" />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col xl="12">
        <adsense
          ad-slot="9585150092"
          ad-format="auto"
          :ad-client="adsenseClient"
          ad-style="display:block">
        </adsense>
      </b-col>
    </b-row>
    <div class="vld-parent">
      <loading :active.sync="loading"
      :can-cancel=false
      :is-full-page="fullPage" />
    </div>
  </b-container>
</template>

<script>
import moment from 'moment'
import Adsense from '@/components/Adsense'

import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  components: { Adsense, Loading },
  name: 'Doctor',
  data () {
    return {
      loading: true,
      fullPage: true,
      doctor: null,
      fullName: '',
      speciality: '',
      notFound: false,
      office: { _id: null, specialities: [] },
      baseUrl: process.env.VUE_APP_BASE_URL,
      googleMapsApiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      adsenseClient: process.env.VUE_APP_GOOGLE_ADSENSE_CLIENT,
      offices: [],
      table: {
        headers: [
          { key: 'day', label: 'Día' },
          { key: 'starts', label: 'Inicio' },
          { key: 'ends', label: 'Termino' }
        ],
        items: []
      }
    }
  },
  metaInfo () {
    const speciality = this.speciality === ''
      ? this.$i18n.t('especiality')
      : this.$i18n.t(`specialities.${this.speciality}`)
    return {
      titleTemplate: `%s | ${this.fullName}`,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: `${this.fullName}. ${speciality} | Listado Médico te ayuda a encontrar médicos y hospitales cercanos a tu ubicación` },
        { name: 'og:url', content: `${this.baseUrl}${this.$route.fullPath}` },
        { name: 'og:type', content: 'article' },
        { name: 'og:title', content: `Listado Médico | ${this.fullName}` },
        { name: 'og:description', content: `${this.fullName}. ${speciality} | Listado Médico te ayuda a encontrar médicos y hospitales cercanos a tu ubicación` },
        { name: 'og:image', content: 'https://s3.amazonaws.com/assets.listadomedico.com/images/default-avatar.png' }
      ]
    }
  },
  async mounted () {
    await this.fetch()
  },
  methods: {
    async fetch () {
      try {
        const response = await this.$http.get(`/doctors/${this.$route.params.id}`)
        this.doctor = response.data
        this.fullName = this.doctor.fullName
        this.offices = this.doctor.offices

        if (this.$route.query && this.$route.query.office) {
          const office = this.doctor.offices.find(of => of._id === this.$route.query.office)
          if (office) this.office = office
        }

        if (this.office && this.office._id) {
          this.speciality = this.office.specialities.join(', ')
          this.table.items = Object.keys(this.office.schedule).map(key => {
            const day = this.office.schedule[key]
            return {
              day: this.$i18n.t(key),
              starts: day.workDay ? moment(day.starts).format('HH:mm') : '-',
              ends: day.workDay ? moment(day.ends).format('HH:mm') : '-'
            }
          })
        }
      } catch (error) {
        if (error.response.status === 404) {
          this.notFound = true
        } else {
          const { errors } = error.response.data
          errors.forEach(function (error) {
            this.$bvToast.toast(error.message, {
              title: 'Error',
              variant: 'danger',
              solid: true
            })
          }.bind(this))
        }
      }
      this.loading = false
    },
    onChangeOffice (value) {
      const office = this.doctor.offices.find(of => of._id === value)
      if (office) {
        this.office = office
        this.table.items = Object.keys(this.office.schedule).map(key => {
          return {
            day: this.$i18n.t(key),
            starts: moment(this.office.schedule[key].starts).format('HH:mm'),
            ends: moment(this.office.schedule[key].ends).format('HH:mm')
          }
        })
      }
    }
  }
}
</script>
